import React, { createContext, useContext, useState, useEffect } from 'react';
import translations, { LANGUAGES } from '@/locales/translations';

const LanguageContext = createContext();

const STORAGE_KEY = 'pivot_language';

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    // Check localStorage first
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved && translations[saved]) {
      return saved;
    }
    // Try to detect browser language
    const browserLang = navigator.language.split('-')[0];
    if (translations[browserLang]) {
      return browserLang;
    }
    return 'en';
  });

  const [isRTL, setIsRTL] = useState(false);

  useEffect(() => {
    // Save to localStorage
    localStorage.setItem(STORAGE_KEY, language);
    
    // Check if RTL language
    const langConfig = LANGUAGES.find(l => l.code === language);
    const rtl = langConfig?.rtl || false;
    setIsRTL(rtl);
    
    // Update document direction
    document.documentElement.dir = rtl ? 'rtl' : 'ltr';
    document.documentElement.lang = language;
  }, [language]);

  // Translation function
  const t = (key, fallback = key) => {
    const translation = translations[language]?.[key];
    if (translation) return translation;
    
    // Fallback to English
    const englishTranslation = translations.en?.[key];
    if (englishTranslation) return englishTranslation;
    
    return fallback;
  };

  // Get current language info
  const getCurrentLanguage = () => {
    return LANGUAGES.find(l => l.code === language) || LANGUAGES[0];
  };

  const value = {
    language,
    setLanguage,
    t,
    isRTL,
    languages: LANGUAGES,
    getCurrentLanguage,
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export default LanguageContext;
